import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { ButtonBase, Collapse, Divider, Stack, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import { deleteBoosterProject, editBoosterProjectTextCreation, getAllBoosterProjects } from 'API/booster-project';
import { Genres, ProductGroups, Routes } from 'enums';
import DeleteTextGenerationProjectDialog from 'features/text-generation/text-generation-projects-overview/delete-text-generation-project-dialog';
import EditTextGenerationProjectDialog from 'features/text-generation/text-generation-projects-overview/edit-text-generation-project-dialog';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import useErrorStore from 'stores/error-store';
import useBoosterToolsGenreStore from 'stores/genre-store';
import { makeStyles } from 'tss-react/mui';
import { ProfileValues } from 'types/profile.types';
import PaiTextField from '../../ui/base-components/pai-text-field';
import { TextGenerationCredentials } from '../genres-config';
import TextGenerationProjectItem from './text-generation-project-item';

interface Props {
  title: string;
  genre: Genres;
  productGroup: ProductGroups;

  resetTextGeneration?: () => void;
}

export interface Project {
  _id: string;
  createDate: string;
  occupation: string;
  projectName: string;
  offer?: string;
  problems?: string;
  content?: string;
  assessment?: string;
  profile?: ProfileValues;
}

const TextGenerationProjectOverview: React.FC<Props> = ({ title, genre, productGroup, resetTextGeneration }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const { setMarketingCampaignGenre } = useBoosterToolsGenreStore((state) => ({ setMarketingCampaignGenre: state.setMarketingCampaignGenre }));
  const { setAppBarNavigation } = useAppBarNavigationStore((state) => ({ setAppBarNavigation: state.setActiveTab }));

  const theme = useTheme();
  const downXL = useMediaQuery(theme.breakpoints.down('xl'));
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const [projects, setProjects] = useState<Project[] | null>([]);
  const [searchParam, setSearchParam] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const [deleteBoosterProjectDialog, setDeleteBoosterProjectDialog] = useState<{ open: boolean; project?: Project }>({
    open: false,
  });

  const [editBoosterProjectDialog, setEditBoosterProjectDialog] = useState<{ open: boolean; project?: Project }>({
    open: false,
    project: undefined,
  });
  const [textGenerationCredentials, setTextGenerationCredentials] = useState<TextGenerationCredentials>({});

  const itemsPerPage = downMD ? 1 : downLG ? 2 : downXL ? 4 : 5;
  const [itemCount, setItemCount] = useState(0);
  const [page, setPage] = useState<number>(1);

  let typingTimer: ReturnType<typeof setTimeout>;

  const hrBoosterLogo = '/dashboard/images/logos/hr_booster_logo_short.svg';
  const leadBoosterLogo = '/dashboard/images/logos/lead_booster_logo_short.svg';

  const fetchProjects = async (newPage: number = page, newSearchParam: string = searchParam) => {
    const res = await getAllBoosterProjects(newPage - 1, itemsPerPage, genre, newSearchParam);

    if (res && !res.error) {
      if (_.isEmpty(res.response) && newSearchParam !== '') {
        setProjects(null);
        setItemCount(0);
        setPage(1);
      } else {
        setProjects(res.response);
        setItemCount(res.total);
        setPage(newPage);
      }
    } else {
      setProjects([]);
      setItemCount(0);
      setPage(1);
      handleOpenErrorDialog(res.error, '/projects/boosters', 'get');
    }
  };

  useEffect(() => {
    if (!_.isNil(genre)) {
      fetchProjects();
    }
  }, [genre, itemsPerPage]);

  /**
   * Search handlers
   */

  const toggleShowSearch = () => {
    setShowSearch((prev) => !prev);

    if (showSearch && searchParam !== '') {
      setSearchParam('');
      fetchProjects(1, '');
    }
  };

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleSearchProjects = async () => {
    fetchProjects(1, searchParam);
  };

  const searchKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      handleSearchProjects();
    }, 500);
  };

  const searchKeyDown = () => {
    clearTimeout(typingTimer);
  };

  /**
   * Pagination handlers
   */

  const handleChangePage = (event: unknown, newPage: number) => {
    fetchProjects(newPage);
  };

  /**
   * Delete booster project dialog handlers
   */

  const handleOpenConfirmDeleteDialog = (project: Project) => {
    return () => {
      setDeleteBoosterProjectDialog({ open: true, project });
    };
  };

  const handleCloseConfirmDeleteDialog = () => {
    setDeleteBoosterProjectDialog((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!deleteBoosterProjectDialog.open) {
      setTimeout(() => {
        setDeleteBoosterProjectDialog((prev) => ({ ...prev, project: undefined }));
      }, 225);
    }
  }, [deleteBoosterProjectDialog.open]);

  const deleteProject = (projectId: string) => {
    return async () => {
      const res = await deleteBoosterProject(projectId);

      if (res && !res.error) {
        const newPage = projects?.length === 1 ? page - 1 : page;
        fetchProjects(newPage);
        handleCloseConfirmDeleteDialog();
      } else {
        handleOpenErrorDialog(res.error, '/projects/boosters/:projectId', 'delete');
      }
    };
  };

  /**
   * Edit booster project handlers
   */

  const handleOpenEditDialog = (project: Project) => {
    return () => {
      const updateTextGenerationCredentials: TextGenerationCredentials = {};

      const offerString = project.offer;
      const contentString = project.content;
      const problems = project.problems;

      if (!_.isNil(offerString)) {
        try {
          const parsedJSON = JSON.parse(offerString);
          updateTextGenerationCredentials.offer = parsedJSON;
        } catch (error) {
          console.log('Error while parsing JSON!', error);
        }
      }

      if (!_.isNil(contentString)) {
        try {
          const parsedJSON = JSON.parse(contentString);
          updateTextGenerationCredentials.content = parsedJSON;
        } catch (error) {
          console.log('Error while parsing JSON!', error);
        }
      }

      if (!_.isNil(problems)) {
        updateTextGenerationCredentials.problems = problems;
      }

      setEditBoosterProjectDialog({ open: true, project });
      setTextGenerationCredentials(updateTextGenerationCredentials);
    };
  };

  const handleCloseEditDialog = () => {
    setEditBoosterProjectDialog((prev) => ({ ...prev, open: false }));
  };

  const handleTextGenerationCredentialsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const updatedCredentials = _.set({ ...textGenerationCredentials }, name, value);
    setTextGenerationCredentials(updatedCredentials);
  };

  useEffect(() => {
    if (!editBoosterProjectDialog.open) {
      setTimeout(() => {
        setEditBoosterProjectDialog((prev) => ({ ...prev, project: undefined }));
        setTextGenerationCredentials({});
      }, 225);
    }
  }, [editBoosterProjectDialog.open]);

  const handleEditProject = async () => {
    if (!_.isNil(editBoosterProjectDialog.project)) {
      const res = await editBoosterProjectTextCreation(
        editBoosterProjectDialog.project?._id,
        textGenerationCredentials.offer,
        textGenerationCredentials.content,
        textGenerationCredentials.problems
      );

      if (res && !res.error) {
        fetchProjects();
        handleCloseEditDialog();
      } else {
        handleOpenErrorDialog(res.error, '/projects/boosters/:projectId', 'put');
      }
    }
  };

  /**
   * Misc handlers
   */

  const handleNewProjectClick = () => {
    if (!_.isNil(resetTextGeneration)) {
      resetTextGeneration();
    }
    if (productGroup === ProductGroups.HR_BOOSTER) {
      if (genre === Genres.JOB_OFFER) {
        if (location.pathname === Routes.HR_BOOSTER_GENERATE_GLOBAL_PROFILE) {
          navigate(Routes.HR_BOOSTER_GENERATE_GLOBAL_PROFILE, { replace: true });
        }

        if (location.pathname === Routes.HR_BOOSTER_GENERATE_TAILORED_PROFILE) {
          navigate(Routes.HR_BOOSTER_GENERATE_TAILORED_PROFILE, { replace: true });
        }

        setAppBarNavigation(NavigationStates.TOOL);
      }
    }

    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      if (location.pathname === Routes.LEAD_BOOSTER_GENERATE_GLOBAL_PROFILE) {
        navigate(Routes.LEAD_BOOSTER_GENERATE_GLOBAL_PROFILE, { replace: true });
      }

      if (location.pathname === Routes.LEAD_BOOSTER_GENERATE_TAILORED_PROFILE) {
        navigate(Routes.LEAD_BOOSTER_GENERATE_TAILORED_PROFILE, { replace: true });
      }

      setMarketingCampaignGenre(genre);
      setAppBarNavigation(NavigationStates.TOOL);
    }
  };

  return (
    <>
      {_.isNil(projects) || !_.isEmpty(projects) ? (
        <Stack gap={2}>
          <Stack gap={1}>
            <Stack gap={0.5}>
              {downMD ? (
                <Stack direction={'column'} gap={1}>
                  <Stack direction={'row'} gap={2}>
                    <Typography className={classes.title}>{title}</Typography>
                    <ButtonBase onClick={toggleShowSearch}>
                      {!showSearch ? <SearchIcon className={classes.icon} /> : <SearchOffIcon className={classes.icon} />}
                    </ButtonBase>
                  </Stack>
                  <Collapse in={showSearch}>
                    <PaiTextField
                      value={searchParam}
                      onChange={handleSearchParamChange}
                      onKeyDown={searchKeyDown}
                      onKeyUp={searchKeyUp}
                      placeholder={t('enterProjectName')}
                      productGroup={productGroup}
                      disableBorder={true}
                    />
                  </Collapse>
                </Stack>
              ) : (
                <Stack direction={'row'} gap={2} alignItems={'center'}>
                  <Stack direction={'row'} alignItems={'center'} gap={1}>
                    <img
                      className={classes.productGroupLogo}
                      src={
                        productGroup === ProductGroups.HR_BOOSTER ? hrBoosterLogo : productGroup === ProductGroups.LEAD_BOOSTER ? leadBoosterLogo : ''
                      }
                      alt="booster logo"
                    />
                    <Typography className={classes.productGroupText}>
                      {productGroup === ProductGroups.HR_BOOSTER
                        ? t('hrBooster')
                        : productGroup === ProductGroups.LEAD_BOOSTER
                        ? t('leadBooster')
                        : ''}
                    </Typography>
                  </Stack>
                  <Divider className={classes.divider} orientation={'vertical'} />
                  <Stack direction={'row'} gap={2} alignItems={'center'}>
                    <Typography className={classes.title}>{title}</Typography>
                    <Collapse in={showSearch} orientation={'horizontal'} timeout={{ appear: 300, enter: 300, exit: 600 }}>
                      <PaiTextField
                        value={searchParam}
                        onChange={handleSearchParamChange}
                        onKeyDown={searchKeyDown}
                        onKeyUp={searchKeyUp}
                        placeholder={t('enterProjectName')}
                        productGroup={productGroup}
                        disableBorder={true}
                        style={{ minWidth: '300px' }}
                      />
                    </Collapse>
                    <ButtonBase onClick={toggleShowSearch} style={{ marginLeft: !showSearch ? '-16px' : undefined }}>
                      {!showSearch ? <SearchIcon className={classes.icon} /> : <SearchOffIcon className={classes.icon} />}
                    </ButtonBase>
                  </Stack>
                  {genre !== Genres.ACTIVE_SOURCING && genre !== Genres.CONNECTION_REQUEST && genre !== Genres.SALES_EMAIL ? (
                    <>
                      <Divider className={classes.divider} orientation={'vertical'} />
                      <ButtonBase className={classes.actionButton} onClick={handleNewProjectClick}>
                        <Stack direction={'row'} alignItems={'center'} gap={1}>
                          <AddIcon className={classes.icon} />
                          {t('newProject')}
                        </Stack>
                      </ButtonBase>
                    </>
                  ) : null}
                </Stack>
              )}
              <div />
            </Stack>

            <Stack gap={2} direction={'row'}>
              {projects?.map((project) => (
                <TextGenerationProjectItem
                  key={project._id}
                  project={project}
                  genre={genre}
                  itemsPerPage={itemsPerPage}
                  productGroup={productGroup}
                  handleOpenEditDialog={handleOpenEditDialog}
                  handleOpenConfirmDeleteDialog={handleOpenConfirmDeleteDialog}
                />
              ))}
            </Stack>
          </Stack>
          <PaiPagination
            total={itemCount}
            itemsPerPage={itemsPerPage}
            page={page}
            hidden={projects?.length === 0 || itemCount <= itemsPerPage}
            handleChangePage={handleChangePage}
          />
        </Stack>
      ) : null}

      <DeleteTextGenerationProjectDialog
        open={deleteBoosterProjectDialog.open}
        project={deleteBoosterProjectDialog.project}
        handleClose={handleCloseConfirmDeleteDialog}
        deleteProject={deleteProject}
      />

      <EditTextGenerationProjectDialog
        open={editBoosterProjectDialog.open}
        genre={genre}
        textGenerationCredentials={textGenerationCredentials}
        handleTextGenerationCredentialsChange={handleTextGenerationCredentialsChange}
        handleClose={handleCloseEditDialog}
        editProject={handleEditProject}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  title: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.greys.medium,
  },
  actionButton: {
    fontSize: theme.typography.pxToRem(16),
    borderRadius: '2px',
    padding: '10px 8px 10px 0',
    color: theme.palette.greys.medium,
    whiteSpace: 'nowrap',
    '&:hover': {
      background: alpha('#000', 0.05),
      color: theme.palette.primary.main,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(24),
  },
  productGroupLogo: {
    height: '40px',
    width: 'auto',
  },
  productGroupText: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginLeft: '16px',
  },
  divider: {
    height: '40px',
    borderRightWidth: '2px',
  },
}));

export default TextGenerationProjectOverview;
